import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  ShieldCheck,
  UserPlus,
  Users,
  Package,
  CheckCircle2,
  XCircle,
  Trash2,
  ArrowLeft,
  LogOut,
  Loader2,
  Clock,
  Mail,
  Sparkles,
  KeyRound,
  Eye,
  EyeOff,
} from "lucide-react";
import { formatMoney, statusMeta, relativeTime } from "@/lib/quotes";

const LOGO_URL = "https://customer-assets-cm19k8pv.emergentagent.net/job_hr-tracker-17/artifacts/p39y89ef_LOGO.jpg%20%281%29.jpeg";

const ROLE_LABEL = {
  admin: "Admin",
  staff: "Staff",
  hr: "Staff",             // legacy — same as staff
  store_manager: "Staff",  // legacy — same as staff
  sales_team: "Sales",
  management: "Management",
  dispatch_team: "Dispatch",
};

const MODULE_LABEL = {
  "team-room": "Team Room",
  presently: "Team Room",
  "petty-cash": "Team Room",
  quotes: "Quotes",
};

function initialsOf(name, email) {
  return (name || email || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
}

export default function AdminPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [pending, setPending] = useState([]);
  const [users, setUsers] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});
  const [pwOpen, setPwOpen] = useState(false);
  const [pwCurrent, setPwCurrent] = useState("");
  const [pwNew, setPwNew] = useState("");
  const [pwConfirm, setPwConfirm] = useState("");
  const [pwShow, setPwShow] = useState(false);
  const [pwSaving, setPwSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, p, u, q] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/pending"),
        api.get("/admin/users"),
        api.get("/quotes"),
      ]);
      setStats(s.data);
      setPending(p.data);
      setUsers(u.data);
      setQuotes(q.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const setB = (key, v) => setBusy((prev) => ({ ...prev, [key]: v }));

  const approve = async (u) => {
    setB(`approve-${u.user_id}`, true);
    try {
      await api.post(`/admin/users/${u.user_id}/approve`);
      toast.success(`Approved ${u.name || u.email}`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setB(`approve-${u.user_id}`, false); }
  };

  const reject = async (u) => {
    setB(`reject-${u.user_id}`, true);
    try {
      await api.post(`/admin/users/${u.user_id}/reject`);
      toast.success(`Request from ${u.email} rejected`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setB(`reject-${u.user_id}`, false); }
  };

  const deleteUser = async (u) => {
    setB(`delete-${u.user_id}`, true);
    try {
      await api.delete(`/admin/users/${u.user_id}`);
      toast.success(`Deleted ${u.name || u.email}`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setB(`delete-${u.user_id}`, false); }
  };

  const deleteQuote = async (q) => {
    setB(`quote-${q.quote_id}`, true);
    try {
      await api.delete(`/quotes/${q.quote_id}`);
      toast.success(`Deleted ${q.quote_no}`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setB(`quote-${q.quote_id}`, false); }
  };

  const resetPwDialog = () => {
    setPwCurrent("");
    setPwNew("");
    setPwConfirm("");
    setPwShow(false);
    setPwSaving(false);
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    if (pwNew.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    if (pwNew !== pwConfirm) {
      toast.error("New password and confirmation do not match");
      return;
    }
    if (pwNew === pwCurrent) {
      toast.error("Pick a password different from the current one");
      return;
    }
    setPwSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: pwCurrent,
        new_password: pwNew,
      });
      toast.success("Password updated. Use the new one from your next sign-in.");
      setPwOpen(false);
      resetPwDialog();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPwSaving(false);
    }
  };

  const kpis = [
    { label: "Pending requests", value: stats?.pending_users ?? "—", icon: UserPlus, tone: "hsl(var(--brand-peach))" },
    { label: "Approved users", value: stats?.approved_users ?? "—", icon: CheckCircle2, tone: "hsl(var(--brand-teal))" },
    { label: "Total quotations", value: stats?.total_quotes ?? "—", icon: Package, tone: "hsl(var(--status-leave))" },
    { label: "Total employees", value: stats?.total_employees ?? "—", icon: Users, tone: "hsl(var(--status-halfday))" },
  ];

  return (
    <div className="min-h-screen grain-bg bloom-bg relative" data-testid="admin-page">
      <div className="relative z-10 max-w-6xl mx-auto px-6 lg:px-12 py-10">
        {/* Header */}
        <header className="flex items-center justify-between gap-4 flex-wrap anim-fade-up">
          <Link to="/" className="flex items-center gap-3.5 group" data-testid="admin-brand-link">
            <div
              className="h-11 w-11 rounded-2xl overflow-hidden shadow-soft ring-1 ring-brand/20 group-hover:scale-105 transition-transform"
              style={{ background: "hsl(var(--brand-teal))" }}
            >
              <img src={LOGO_URL} alt="SpaceAttire Teams" className="h-full w-full object-cover" />
            </div>
            <div>
              <div className="font-display text-lg font-bold leading-none tracking-tight">SpaceAttire Teams</div>
              <div className="eyebrow mt-1 whitespace-nowrap">Admin console</div>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            <Link
              to="/"
              data-testid="admin-back-to-hub"
              className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-muted-foreground hover:text-foreground font-mono-mini transition-colors px-3 py-2 rounded-lg hover:bg-secondary"
            >
              <ArrowLeft className="h-3 w-3" /> Hub
            </Link>

            <Dialog
              open={pwOpen}
              onOpenChange={(open) => {
                setPwOpen(open);
                if (!open) resetPwDialog();
              }}
            >
              <DialogTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  data-testid="admin-change-password-btn"
                  className="h-9 rounded-lg border-border hover:bg-secondary"
                >
                  <KeyRound className="h-3.5 w-3.5 mr-1.5" />
                  Change password
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md rounded-2xl">
                <DialogHeader>
                  <DialogTitle className="font-display text-2xl tracking-tight">Change password</DialogTitle>
                  <DialogDescription>
                    Rotate the password for <span className="font-medium text-foreground">{user?.email}</span>. Minimum 8 characters. You'll stay signed in on this device.
                  </DialogDescription>
                </DialogHeader>

                <form onSubmit={submitPassword} className="space-y-4 mt-2" data-testid="change-password-form">
                  <div className="space-y-1.5">
                    <Label htmlFor="pw-current" className="text-xs uppercase tracking-widest text-muted-foreground font-mono-mini">
                      Current password
                    </Label>
                    <Input
                      id="pw-current"
                      type={pwShow ? "text" : "password"}
                      value={pwCurrent}
                      onChange={(e) => setPwCurrent(e.target.value)}
                      required
                      autoComplete="current-password"
                      data-testid="pw-current-input"
                      className="rounded-xl"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="pw-new" className="text-xs uppercase tracking-widest text-muted-foreground font-mono-mini">
                      New password
                    </Label>
                    <Input
                      id="pw-new"
                      type={pwShow ? "text" : "password"}
                      value={pwNew}
                      onChange={(e) => setPwNew(e.target.value)}
                      required
                      minLength={8}
                      autoComplete="new-password"
                      data-testid="pw-new-input"
                      className="rounded-xl"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="pw-confirm" className="text-xs uppercase tracking-widest text-muted-foreground font-mono-mini">
                      Confirm new password
                    </Label>
                    <Input
                      id="pw-confirm"
                      type={pwShow ? "text" : "password"}
                      value={pwConfirm}
                      onChange={(e) => setPwConfirm(e.target.value)}
                      required
                      minLength={8}
                      autoComplete="new-password"
                      data-testid="pw-confirm-input"
                      className="rounded-xl"
                    />
                  </div>

                  <button
                    type="button"
                    onClick={() => setPwShow((s) => !s)}
                    data-testid="pw-show-toggle"
                    className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {pwShow ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                    {pwShow ? "Hide passwords" : "Show passwords"}
                  </button>

                  <DialogFooter className="pt-2">
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => { setPwOpen(false); resetPwDialog(); }}
                      className="rounded-xl"
                      data-testid="pw-cancel-btn"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={pwSaving}
                      className="rounded-xl bg-brand hover:opacity-90 text-white shadow-soft"
                      data-testid="pw-submit-btn"
                    >
                      {pwSaving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <KeyRound className="h-4 w-4 mr-1.5" />}
                      Update password
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>

            <Button
              variant="ghost"
              size="sm"
              onClick={async () => { await logout(); navigate("/"); }}
              data-testid="admin-logout-btn"
              className="h-9 rounded-lg text-muted-foreground hover:text-foreground"
            >
              <LogOut className="h-3.5 w-3.5 mr-1.5" />
              Sign out
            </Button>
          </div>
        </header>

        {/* Hero */}
        <section className="mt-12 max-w-3xl anim-fade-up anim-delay-1">
          <div className="flex items-center gap-2 eyebrow">
            <ShieldCheck className="h-3.5 w-3.5" /> Owner console
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mt-3 leading-[1.05]">
            Everything <span className="text-brand italic font-normal">across</span> the workspace
          </h1>
          <p className="text-base text-muted-foreground mt-4 leading-relaxed">
            Signed in as <span className="font-medium text-foreground">{user?.email}</span>. Approve or reject signup requests, delete users, and remove any quotation across every module.
          </p>
        </section>

        {/* KPIs */}
        <section className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="admin-kpis">
          {kpis.map(({ label, value, icon: Icon, tone }, i) => (
            <Card key={label} className={`p-5 rounded-2xl border-border bg-card shadow-soft anim-fade-up anim-delay-${(i + 2) > 4 ? 4 : (i + 2)}`}>
              <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-mono-mini">{label}</div>
                <Icon className="h-4 w-4" style={{ color: tone }} />
              </div>
              <div className="font-display text-4xl font-bold tabular-nums tracking-tighter mt-3" style={{ color: tone }}>
                {loading ? "—" : value}
              </div>
            </Card>
          ))}
        </section>

        {/* Tabs */}
        <section className="mt-10 anim-fade-up anim-delay-3">
          <Tabs defaultValue="pending">
            <TabsList className="rounded-xl bg-secondary p-1 h-auto flex-wrap">
              <TabsTrigger value="pending" data-testid="tab-pending" className="rounded-lg px-4 py-2 data-[state=active]:bg-card data-[state=active]:shadow-soft">
                <Clock className="h-3.5 w-3.5 mr-2" />
                Pending
                {pending.length > 0 && (
                  <span className="ml-2 h-5 min-w-5 px-1.5 rounded-full bg-brand text-white text-[10px] font-mono-mini grid place-items-center">
                    {pending.length}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="users" data-testid="tab-users" className="rounded-lg px-4 py-2 data-[state=active]:bg-card data-[state=active]:shadow-soft">
                <Users className="h-3.5 w-3.5 mr-2" /> Users
              </TabsTrigger>
              <TabsTrigger value="quotes" data-testid="tab-quotes" className="rounded-lg px-4 py-2 data-[state=active]:bg-card data-[state=active]:shadow-soft">
                <Package className="h-3.5 w-3.5 mr-2" /> Quotations
              </TabsTrigger>
            </TabsList>

            {/* Pending */}
            <TabsContent value="pending" className="mt-6" data-testid="pending-panel">
              {loading ? (
                <div className="py-16 text-center text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin inline-block mr-2" /> Loading…
                </div>
              ) : pending.length === 0 ? (
                <Card className="p-12 rounded-2xl border-dashed border-2 border-border text-center bg-card/60">
                  <Sparkles className="h-6 w-6 mx-auto text-muted-foreground" />
                  <div className="font-display text-xl font-bold mt-3">All caught up</div>
                  <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
                    No signup requests pending. New requests will appear here for your approval.
                  </p>
                </Card>
              ) : (
                <ul className="space-y-3">
                  {pending.map((u) => (
                    <li key={u.user_id} data-testid={`pending-row-${u.user_id}`}>
                      <Card className="p-5 rounded-2xl border-border shadow-soft flex items-center gap-4 flex-wrap">
                        <Avatar className="h-11 w-11 ring-2 ring-brand/15">
                          {u.picture ? <AvatarImage src={u.picture} alt={u.name} /> : null}
                          <AvatarFallback className="text-xs bg-brand/15 text-brand font-semibold">
                            {initialsOf(u.name, u.email)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <div className="font-medium text-sm truncate">{u.name}</div>
                            <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm font-mono-mini bg-secondary text-muted-foreground">
                              {ROLE_LABEL[u.role] || u.role}
                            </span>
                            {u.requested_module && (
                              <span
                                className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm font-mono-mini"
                                style={{ background: "hsl(var(--brand-peach) / 0.35)", color: "hsl(var(--status-leave))" }}
                              >
                                → {MODULE_LABEL[u.requested_module] || u.requested_module}
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground mt-1 flex items-center gap-3 flex-wrap">
                            <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" /> {u.email}</span>
                            <span>Requested {relativeTime(u.created_at)}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 ml-auto">
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                data-testid={`reject-${u.user_id}`}
                                disabled={busy[`reject-${u.user_id}`]}
                                className="h-9 rounded-lg border-destructive/30 text-destructive hover:bg-destructive/10 hover:text-destructive"
                              >
                                <XCircle className="h-4 w-4 mr-1.5" /> Reject
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Reject request?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This will permanently delete the signup request from <span className="font-medium">{u.email}</span>. They'll need to sign up again if they want access later.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Keep it</AlertDialogCancel>
                                <AlertDialogAction
                                  data-testid={`confirm-reject-${u.user_id}`}
                                  className="bg-destructive text-destructive-foreground hover:opacity-90"
                                  onClick={() => reject(u)}
                                >
                                  Yes, reject
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                          <Button
                            size="sm"
                            data-testid={`approve-${u.user_id}`}
                            disabled={busy[`approve-${u.user_id}`]}
                            className="h-9 rounded-lg bg-brand hover:opacity-90 text-white shadow-soft"
                            onClick={() => approve(u)}
                          >
                            {busy[`approve-${u.user_id}`]
                              ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                              : <CheckCircle2 className="h-4 w-4 mr-1.5" />}
                            Approve
                          </Button>
                        </div>
                      </Card>
                    </li>
                  ))}
                </ul>
              )}
            </TabsContent>

            {/* All users */}
            <TabsContent value="users" className="mt-6" data-testid="users-panel">
              {loading ? (
                <div className="py-16 text-center text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin inline-block mr-2" /> Loading…
                </div>
              ) : (
                <Card className="rounded-2xl border-border shadow-soft overflow-hidden">
                  <ul className="divide-y divide-border">
                    {users.map((u) => {
                      const isMe = u.user_id === user?.user_id;
                      const isOtherAdmin = u.role === "admin" && !isMe;
                      const canDelete = !isMe && !isOtherAdmin;
                      return (
                        <li key={u.user_id} data-testid={`user-row-${u.user_id}`} className="px-5 py-4 flex items-center gap-4 flex-wrap">
                          <Avatar className="h-10 w-10">
                            {u.picture ? <AvatarImage src={u.picture} alt={u.name} /> : null}
                            <AvatarFallback className={`text-xs ${u.role === "admin" ? "bg-brand text-white" : "bg-secondary"}`}>
                              {initialsOf(u.name, u.email)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <div className="font-medium text-sm truncate">{u.name}</div>
                              <span className={[
                                "text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm font-mono-mini",
                                u.role === "admin"
                                  ? "bg-brand/15 text-brand"
                                  : "bg-secondary text-muted-foreground",
                              ].join(" ")}>
                                {ROLE_LABEL[u.role] || u.role}
                              </span>
                              {u.status === "pending" && (
                                <span
                                  className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm font-mono-mini font-semibold"
                                  style={{ background: "hsl(var(--brand-peach) / 0.35)", color: "hsl(var(--status-leave))" }}
                                >
                                  Pending
                                </span>
                              )}
                              {isMe && (
                                <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm font-mono-mini bg-secondary text-muted-foreground">
                                  You
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-muted-foreground mt-1 truncate">{u.email}</div>
                          </div>
                          <div className="text-right">
                            {canDelete ? (
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    data-testid={`delete-user-${u.user_id}`}
                                    disabled={busy[`delete-${u.user_id}`]}
                                    className="h-9 rounded-lg text-destructive hover:bg-destructive/10 hover:text-destructive"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Delete this user?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      <span className="font-medium">{u.name}</span> ({u.email}) will be permanently removed. Any content they created in the modules stays in place.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction
                                      data-testid={`confirm-delete-user-${u.user_id}`}
                                      className="bg-destructive text-destructive-foreground hover:opacity-90"
                                      onClick={() => deleteUser(u)}
                                    >
                                      Delete user
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            ) : (
                              <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono-mini">
                                {isMe ? "Protected" : "Admin"}
                              </span>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </Card>
              )}
            </TabsContent>

            {/* All quotations */}
            <TabsContent value="quotes" className="mt-6" data-testid="quotes-panel">
              {loading ? (
                <div className="py-16 text-center text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin inline-block mr-2" /> Loading…
                </div>
              ) : quotes.length === 0 ? (
                <Card className="p-12 rounded-2xl border-dashed border-2 border-border text-center bg-card/60">
                  <Package className="h-6 w-6 mx-auto text-muted-foreground" />
                  <div className="text-sm text-muted-foreground mt-3">No quotations yet.</div>
                </Card>
              ) : (
                <Card className="rounded-2xl border-border shadow-soft overflow-hidden">
                  <ul className="divide-y divide-border">
                    {quotes.map((q) => {
                      const s = statusMeta(q.status);
                      return (
                        <li key={q.quote_id} data-testid={`admin-quote-row-${q.quote_id}`} className="px-5 py-4 flex items-center gap-4 flex-wrap">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-mono-mini text-[11px] tabular-nums text-muted-foreground">{q.quote_no}</span>
                              <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm font-mono-mini" style={{ background: s.bg, color: s.color }}>
                                {s.label}
                              </span>
                            </div>
                            <div className="mt-1 text-sm font-medium truncate">{q.customer?.name}</div>
                            <div className="text-xs text-muted-foreground mt-0.5 truncate">
                              {q.created_by_name || "—"} · {relativeTime(q.created_at)}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="font-display text-lg font-bold tabular-nums" style={{ color: s.color }}>
                              {formatMoney(q.final_price || 0)}
                            </div>
                            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">
                              {q.items?.length ?? 0} items
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Link
                              to={`/quotes/${q.quote_id}`}
                              className="text-xs text-brand hover:opacity-75 transition-opacity underline underline-offset-4"
                              data-testid={`admin-view-quote-${q.quote_id}`}
                            >
                              Open
                            </Link>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  data-testid={`admin-delete-quote-${q.quote_id}`}
                                  disabled={busy[`quote-${q.quote_id}`]}
                                  className="h-9 rounded-lg text-destructive hover:bg-destructive/10 hover:text-destructive"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete this quotation?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    <span className="font-medium">{q.quote_no}</span> for <span className="font-medium">{q.customer?.name}</span> will be permanently removed, including dispatch history and images.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction
                                    data-testid={`confirm-delete-quote-${q.quote_id}`}
                                    className="bg-destructive text-destructive-foreground hover:opacity-90"
                                    onClick={() => deleteQuote(q)}
                                  >
                                    Delete quotation
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </Card>
              )}
            </TabsContent>
          </Tabs>
        </section>
      </div>
    </div>
  );
}
